/**
 * Authentication types
 */

export type NumberFormat = 'tenant_default' | 'comma_dot' | 'dot_comma';
export type DateFormat = 'tenant_default' | 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';
// 'tenant_default' inherits the tenant clock default (resolved in
// UserPreferencesContext); '12h'/'24h' are explicit user overrides.
export type TimeFormat = 'tenant_default' | '12h' | '24h';
export type SupportedLanguage = 'en' | 'es' | 'he';
/** First day of week. locale_default = derive from country/language. */
export type WeekStartDay = 'locale_default' | 'sunday' | 'monday';
export type ResolvedWeekStartDay = 'sunday' | 'monday';

// Measurement unit types (see measurement_units.config.json)
export type LengthUnit = 'tenant_default' | 'cm' | 'in';
export type DistanceUnit = 'tenant_default' | 'km' | 'mi';
export type WeightUnit = 'tenant_default' | 'kg' | 'lb';
export type VolumeUnit = 'tenant_default' | 'm3' | 'ft3';
export type LiquidVolumeUnit = 'tenant_default' | 'l' | 'gal';
export type AreaUnit = 'tenant_default' | 'm2' | 'ft2';
export type TemperatureUnit = 'tenant_default' | 'c' | 'f';

// Resolved unit types (without tenant_default)
export type ResolvedLengthUnit = 'cm' | 'in';
export type ResolvedDistanceUnit = 'km' | 'mi';
export type ResolvedWeightUnit = 'kg' | 'lb';
export type ResolvedVolumeUnit = 'm3' | 'ft3';
export type ResolvedLiquidVolumeUnit = 'l' | 'gal';
export type ResolvedAreaUnit = 'm2' | 'ft2';
export type ResolvedTemperatureUnit = 'c' | 'f';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface UserPreferences {
  theme_mode?: ThemeMode;
  // Additional preferences stored as JSON
  [key: string]: unknown;
}

/** Module IDs that can be enabled/disabled via settings */
export type ToggleableModuleId = 'work' | 'pipeline' | 'recruiting' | 'accounting' | 'ai';

export interface User {
  id: string;
  tenant_id: string | null;  // Nullable for pending_setup users
  email: string;
  handle: string | null;
  first_name: string;
  last_name: string;
  full_name: string;
  status: 'active' | 'inactive' | 'pending' | 'locked' | 'pending_verification' | 'pending_setup';
  is_admin: boolean;  // Tenant admin access
  is_sysadmin: boolean;
  country: string | null;  // ISO 3166-1 alpha-2 country code
  preferred_language: SupportedLanguage;
  number_format: NumberFormat;
  date_format: DateFormat;
  time_format: TimeFormat;
  timezone: string;
  week_start_day: WeekStartDay;  // raw preference (locale_default = auto)
  resolved_week_start_day: ResolvedWeekStartDay;  // concrete value to render with
  preferred_currency: string | null;  // ISO 4217 currency code (null = use tenant default)
  // Measurement unit preferences
  unit_length: LengthUnit;
  unit_distance: DistanceUnit;
  unit_weight: WeightUnit;
  unit_volume: VolumeUnit;
  unit_liquid_volume: LiquidVolumeUnit;
  unit_area: AreaUnit;
  unit_temperature: TemperatureUnit;
  preferences: UserPreferences;
  profile_image_file_id: string | null;
  profile_image_url: string | null;
  primary_company_id: string | null;
  has_password: boolean;
  sso_provider: string | null;
  last_login_at: string | null;
  job_function: string | null;
  job_function_custom: string | null;
  job_level: string | null;
  onboarding_completed_at: string | null;
  created_at: string;
  updated_at: string;
  /** User's enabled modules. NULL means all available modules within ceiling. */
  enabled_modules: ToggleableModuleId[] | null;
  /** Module ceiling set by admin/inviter. NULL means no ceiling (all tenant modules available). */
  module_ceiling: string[] | null;
  /** Platform's available modules (from /auth/me). NULL means all modules. */
  platform_available_modules: string[] | null;
  /** Tenant's enabled modules (from /auth/me). NULL means all modules. */
  tenant_enabled_modules: ToggleableModuleId[] | null;
  /** Tenant slug for constructing career portal URL */
  tenant_slug: string | null;
  /** True if tenant needs company profile configuration (new tenant) */
  tenant_needs_onboarding: boolean;
  /** Module names where user has settings.manage permission (e.g., ['sales', 'tasks']) */
  module_admin_roles: string[];
  /** Per-module access levels set by an admin. NULL means every module defaults to 'member'. */
  module_access_levels: Record<string, 'viewer' | 'member' | 'admin'> | null;
  /** Module the user lands on after login (locked starting point). NULL means the default landing. */
  starting_module: string | null;
  /** When true, the user may bypass guardrails like credit holds and pricing limits. */
  can_override: boolean;
  /** Warehouse the user is restricted to. NULL means no restriction. */
  warehouse_id: string | null;
  /**
   * Per-user grant for the Developer Portal (API keys). The developer module is
   * hidden with no tenant-wide toggle, so access is granted only per-user by an
   * admin. Admins have implicit access regardless of this flag.
   */
  developer_portal_enabled: boolean;
}

/**
 * Whether the user may access the Developer Portal (API keys). Mirrors the
 * server-side `require_developer_access` gate exactly: tenant admins and
 * sysadmins have implicit access, plus any non-admin granted per-user via
 * `developer_portal_enabled`. Single source of truth for all three UI surfaces
 * (route guard, user-menu item, module picker) so they never drift apart.
 */
export function hasDeveloperPortalAccess(
  user: Pick<User, 'is_admin' | 'is_sysadmin' | 'developer_portal_enabled'> | null | undefined,
): boolean {
  return Boolean(user && (user.is_admin || user.is_sysadmin || user.developer_portal_enabled));
}

export interface Company {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  status: 'active' | 'inactive';
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  timezone?: string;
}

/** A server response that both authenticates the caller and identifies them. */
export interface AuthSessionResponse {
  user: User;
  access_token: string;
  refresh_token: string;
}

export interface LoginResponse extends AuthSessionResponse {
  /** URL to redirect user to after login (when on main domain with custom domain configured) */
  redirect_to_domain: string | null;
  /** Present only when login also confirmed a TOTP enrollment — shown once. */
  recovery_codes?: string[];
}

/** MFA method an authenticator (TOTP) or emailed one-time code. */
export type MfaMethod = 'totp' | 'email';

/**
 * Returned by /auth/login when the password was correct but MFA is still
 * outstanding. Carries the short-lived challenge_token — no JWT yet.
 */
export interface MfaChallengeResponse {
  /** Set when the user is enrolled and must enter a code. */
  mfa_required?: boolean;
  /** Set when MFA is required but the user has not enrolled yet. */
  mfa_enrollment_required?: boolean;
  /** The enrolled method, present with mfa_required. */
  mfa_method?: MfaMethod;
  /** Methods the user may enroll in, present with mfa_enrollment_required. */
  allowed_methods?: MfaMethod[];
  challenge_token: string;
  /**
   * The known user's account language. The 2FA card renders before a JWT is
   * issued (so the authenticated-language reconciliation can't run yet); this
   * lets the card switch into the user's chosen language instead of the pre-auth
   * GeoIP/browser guess. See useApplyChallengeLanguage.
   */
  preferred_language?: string;
  /**
   * Dev-only: set by the backend ONLY when running locally (non-production +
   * localhost host). When true the challenge card offers a "skip 2FA" link
   * that completes login without a second factor. Never present in production.
   */
  localhost_exemption_available?: boolean;
}

/** /auth/login resolves to either a completed login or an MFA challenge. */
export type LoginApiResult = LoginResponse | MfaChallengeResponse;

/** Type guard: did /auth/login return an MFA challenge rather than tokens? */
export function isMfaChallenge(result: LoginApiResult): result is MfaChallengeResponse {
  return 'challenge_token' in result;
}

/** Outcome of AuthContext.login — either authenticated, or paused for MFA. */
export type LoginOutcome =
  | { kind: 'authenticated'; redirectUrl: string | null }
  | { kind: 'mfa'; challenge: MfaChallengeResponse };

/** A browser the user has trusted to skip MFA. */
export interface MfaTrustedDevice {
  id: string;
  device_label: string | null;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  /** True for the browser making the request (matched via its mfa_device cookie). */
  is_current: boolean;
}

/** MFA enrollment status for the signed-in user. */
export interface MfaStatus {
  enabled: boolean;
  method: MfaMethod | null;
  required: boolean;
  recovery_codes_remaining: number;
  allowed_methods: MfaMethod[];
}

/** Factor selected by the server for a sensitive-action confirmation. */
export type StepUpMethod = 'password' | 'totp' | 'email';

export interface StepUpChallenge {
  challenge_token: string;
  method: StepUpMethod;
}

export interface StepUpProof {
  proof_token: string;
  expires_in_seconds: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_name?: string;  // Only required when creating new tenant
  join_tenant_id?: string;  // UUID of tenant to join
  join_company_id?: string;  // UUID of company to join
  invitation_token?: string;  // If joining via invitation
  preferred_language?: string;  // Language chosen on the registration form (carries to the verification email)
  terms_accepted: boolean;
  content_processing_consent: boolean;
}

export interface RegisterResponse {
  message_code: string;
  email_sent: boolean;
  user_id: string;
  tenant_id: string;
}

// Registration options types
export interface InvitationOption {
  type: 'invitation';
  tenant_id: string;
  tenant_name: string;
  company_id: string;
  company_name: string;
  is_admin: boolean;
}

export interface DomainMatchOption {
  type: 'domain_match';
  tenant_id: string;
  tenant_name: string;
  companies: Array<{ id: string; name: string }>;
}

export type RegistrationOption = InvitationOption | DomainMatchOption;

export interface CheckRegistrationRequest {
  email: string;
  invitation_token?: string;
}

export interface CheckRegistrationResponse {
  email_available: boolean;
  options: RegistrationOption[];
  /** Set when email exists on a subdomain tenant */
  existing_subdomain?: string;
  /** Full URL to the subdomain login page */
  existing_subdomain_url?: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  message_code: string;
  email?: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  message: string;
}

export interface RegisterWithInvitationRequest {
  invitation_token: string;
  first_name: string;
  last_name: string;
  password: string;
  /** Explicit login-screen language choice only — never the ambient UI language (backend defaults to 'en'). */
  preferred_language?: string;
  terms_accepted: boolean;
  content_processing_consent: boolean;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export type UpdateUserData = Partial<Pick<User, 'first_name' | 'last_name' | 'preferences' | 'profile_image_file_id' | 'preferred_language' | 'number_format' | 'date_format' | 'time_format' | 'timezone' | 'week_start_day' | 'country' | 'preferred_currency' | 'unit_length' | 'unit_distance' | 'unit_weight' | 'unit_volume' | 'unit_liquid_volume' | 'unit_area' | 'unit_temperature' | 'enabled_modules' | 'primary_company_id'>>;

export interface AuthContextType extends AuthState {
  /** Login. Resolves to an authenticated outcome, or an MFA challenge to satisfy. */
  login: (credentials: LoginRequest) => Promise<LoginOutcome>;
  /** Establish a session from a completed-login response (used by the MFA flow). */
  completeMfaLogin: (response: LoginResponse) => Promise<string | null>;
  /**
   * Establish a session for a NEW identity (invitation acceptance). Unlike
   * `completeMfaLogin`, this also purges the prior identity's emulation and
   * demo state — a shared browser may still hold them.
   */
  establishSessionForNewIdentity: (response: AuthSessionResponse) => Promise<void>;
  register: (data: RegisterRequest) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  updateUser: (data: UpdateUserData) => Promise<User>;
}
